import dayjs from 'dayjs';
import { ROUTES } from '@/routes/paths';
import type { DealTimePoint, MarketCrop, MarketSide } from '@/types/agentAnalytics';

// Чистые трансформации данных раздела «Рынок» агента для графика сделок (@mantine/charts) и шкалы цен.
// Без React — покрыто юнит-тестами. Цены ₽/т без НДС.

export interface DealsChartPoint {
  readonly label: string;
  /** Типичная (медианная) цена сделок в шаге, ₽/т; null — сделок в шаге не было. */
  readonly median: number | null;
  readonly count: number;
}

/** Точки ряда сделок → данные CompositeChart: типичная цена линией и число сделок столбиками. */
export function dealsChartData(series: readonly DealTimePoint[], bucket: string): DealsChartPoint[] {
  const fmt = bucket === 'month' ? 'MMM YY' : 'DD.MM';
  return series.map((point) => ({ label: dayjs(point.bucket).format(fmt), median: point.median, count: point.count }));
}

export interface PriceRangeMarkers {
  readonly min: number;
  readonly max: number;
  readonly p25: number | null;
  readonly median: number | null;
  readonly p75: number | null;
  /** Позиция цены на шкале [min, max] в процентах (0..100), безопасно при min==max. */
  readonly pct: (value: number) => number;
}

/** Маркеры распределения цены стороны рынка (min–P25–median–P75–max) для горизонтальной шкалы. */
export function priceRange(side: MarketSide | null | undefined): PriceRangeMarkers | null {
  if (!side || side.min?.price == null || side.max?.price == null) {
    return null;
  }
  const min = side.min.price;
  const max = side.max.price;
  const span = max - min;
  return {
    min,
    max,
    p25: side.p25,
    median: side.median,
    p75: side.p75,
    pct: (value: number) => (span <= 0 ? 0 : Math.max(0, Math.min(100, ((value - min) / span) * 100))),
  };
}

/** Итоги рынка для панели «Главной» агента: сколько предложений о продаже и прайс-листов покупателей. */
export function marketTotals(crops: readonly MarketCrop[]): { readonly sell: number; readonly buy: number } {
  return crops.reduce(
    (acc, crop) => ({ sell: acc.sell + (crop.sell?.count ?? 0), buy: acc.buy + (crop.buy?.count ?? 0) }),
    { sell: 0, buy: 0 },
  );
}

/** Культуры, где больше всего предложений о продаже, для «Рынка сейчас» на «Главной» агента. */
export function topMarketCrops(crops: readonly MarketCrop[], limit = 5): MarketCrop[] {
  return crops
    .filter((crop) => (crop.sell?.count ?? 0) > 0)
    .sort((a, b) => (b.sell?.count ?? 0) - (a.sell?.count ?? 0) || a.goodsType.localeCompare(b.goodsType, 'ru'))
    .slice(0, limit);
}

/** Адрес экрана культуры в разделе «Рынок»: культура в адресе, чтобы экран открывался по ссылке и «Назад». */
export function marketCropPath(goodsType: string): string {
  return `${ROUTES.APP_AGENT_DASHBOARD}?crop=${encodeURIComponent(goodsType)}`;
}
